/**
 * Pure utilities for computing course and section week indices from
 * course timing fields.
 *
 * These functions encode the shared week-calculation logic used by both
 * the webapp (via React context) and server-side scheduled functions.
 * Keeping them here avoids divergence between client and server.
 */

import {ONE_DAY_MS, ONE_HOUR_MS, ONE_WEEK_MS} from '../utils/time';
import {
  computeSectionInstant,
  isFollowingCourseTimezone,
  type SectionClock,
} from './slotTime';

/**
 * Default timezone offset (in hours) applied to `firstSectionTimestamp`
 * values that lack an explicit timezone indicator. Per the course document
 * schema, bare timestamps are hardcoded to UTC-7 (PDT).
 */
const DEFAULT_TIMEZONE_OFFSET_HOURS = -7;

const ONE_INDEX_OFFSET = 1;

/**
 * Parses a `firstSectionTimestamp` string into a `Date`.
 *
 * If the string already contains a timezone indicator (e.g. `Z`,
 * `+00:00`, `-07:00`) it is parsed as-is. Otherwise it is interpreted
 * as UTC-7 (PDT), which matches the course document schema convention.
 *
 * Returns `undefined` for invalid or empty input.
 */
export function parseFirstSectionTimestamp(raw: string): Date | undefined {
  if (!raw) return undefined;

  const hasTimezone = /Z|[+-]\d{2}:\d{2}$/.test(raw);

  if (hasTimezone) {
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  // No timezone — treat as PDT (UTC-7)
  const utcDate = new Date(raw + 'Z');
  if (Number.isNaN(utcDate.getTime())) return undefined;

  const offsetMs = DEFAULT_TIMEZONE_OFFSET_HOURS * ONE_HOUR_MS;
  return new Date(utcDate.getTime() - offsetMs);
}

/**
 * Calculates the timestamp of when the last section of a week starts.
 *
 * `lastSection = firstSection + sectionTimeDelta` (in hours). This is
 * the anchor point for the section-week formula.
 *
 * @param firstSectionTimestamp - Course anchor, parsed by
 *   {@link parseFirstSectionTimestamp}.
 * @param sectionTimeDelta - Hours from the first to the last section of a
 *   week.
 * @param weekIndex - 0-based section week. Omitted: week 0.
 * @param clock - The course's {@link SectionClock}. Omitted: fixed-instant
 *   recurrence.
 * @return The instant, or `undefined` when the anchor is missing or
 *   unparseable.
 * @throws Never.
 */
export function calcLastSectionTimestamp({
  firstSectionTimestamp,
  sectionTimeDelta,
  weekIndex = 0,
  clock = {},
}: {
  firstSectionTimestamp: string;
  sectionTimeDelta: number;
  weekIndex?: number;
  clock?: SectionClock;
}): Date | undefined {
  const firstSection = parseFirstSectionTimestamp(firstSectionTimestamp);
  if (!firstSection) return undefined;

  return computeSectionInstant({
    firstSectionTimestamp: firstSection.toISOString(),
    timeIndex: sectionTimeDelta,
    weekIndex,
    ...clock,
  });
}

/**
 * The instant a week's last section ends: `computeSectionInstant` at
 * `timeIndex = sectionTimeDelta`, plus the one-hour section length.
 *
 * Uses `computeSectionInstant` (a `Date.parse` of `firstSectionTimestamp`) so it
 * matches the webapp's `getLastSectionOfWeekDate` for timezone-carrying
 * timestamps. For a bare (tz-less) timestamp this differs from
 * {@link parseFirstSectionTimestamp}'s UTC-7 default — pass a timestamp with an
 * explicit offset to keep the server and webapp instants identical.
 *
 * @param firstSectionTimestamp - Course anchor ISO string (week 1's first section).
 * @param sectionTimeDelta - Hours from the first to the last section of a week
 *   (the last section's time index).
 * @param sectionWeek - 1-based section week.
 * @param clock - The course's {@link SectionClock}. Omitted: fixed-instant
 *   recurrence.
 * @returns Epoch ms of the end of the week's last section.
 * @throws If `firstSectionTimestamp` is unparseable (from `computeSectionInstant`).
 */
export function getWeekLastSectionEndMs({
  firstSectionTimestamp,
  sectionTimeDelta,
  sectionWeek,
  clock = {},
}: {
  firstSectionTimestamp: string;
  sectionTimeDelta: number;
  sectionWeek: number;
  clock?: SectionClock;
}): number {
  const lastSectionStartMs = computeSectionInstant({
    firstSectionTimestamp,
    timeIndex: sectionTimeDelta,
    weekIndex: sectionWeek - 1,
    ...clock,
  }).getTime();
  return lastSectionStartMs + ONE_HOUR_MS;
}

/**
 * The instant a week's SL section feedback opens: the week's last section end
 * (see {@link getWeekLastSectionEndMs}) plus `delayDays`. At the course default
 * delay this equals `SlSectionFeedbackPage`'s `feedbackOpensAt`.
 *
 * @param firstSectionTimestamp - Course anchor ISO string (week 1's first section).
 * @param sectionTimeDelta - Hours from the first to the last section of a week
 *   (the last section's time index).
 * @param sectionWeek - 1-based section week.
 * @param delayDays - Days after the last section ends before feedback opens
 *   (course `sectionFeedbackDelayDays`).
 * @param clock - The course's {@link SectionClock}. Omitted: fixed-instant
 *   recurrence.
 * @returns Epoch ms of the feedback-open instant.
 * @throws If `firstSectionTimestamp` is unparseable (from `computeSectionInstant`).
 */
export function getSectionFeedbackOpenMs({
  firstSectionTimestamp,
  sectionTimeDelta,
  sectionWeek,
  delayDays,
  clock,
}: {
  firstSectionTimestamp: string;
  sectionTimeDelta: number;
  sectionWeek: number;
  delayDays: number;
  clock?: SectionClock;
}): number {
  return (
    getWeekLastSectionEndMs({
      firstSectionTimestamp,
      sectionTimeDelta,
      sectionWeek,
      clock,
    }) +
    delayDays * ONE_DAY_MS
  );
}

/**
 * UTC offset of "Anywhere on Earth" (UTC-12), the last time zone in which a
 * calendar day ends.
 */
const AOE_UTC_OFFSET_MS = -12 * ONE_HOUR_MS;

/**
 * An instant at which a Sunday ends Anywhere on Earth. 1970-01-05T00:00Z was a
 * Monday, so Monday 00:00 AoE is that instant shifted by the AoE offset.
 */
const END_OF_SUNDAY_AOE_ANCHOR_MS = 4 * ONE_DAY_MS - AOE_UTC_OFFSET_MS;

/**
 * The next weekly peer-shadow review deadline: the first end of the course's
 * due weekday, Anywhere on Earth, strictly after `afterMs`. An `afterMs` that
 * is exactly a deadline instant returns the following one.
 *
 * @param afterMs - The instant (epoch ms) the deadline must come after.
 * @param dueWeekday - The weekday whose end is the deadline, numbered as
 *   `Date.prototype.getDay` does (course `shadowReviewDueWeekday`). A value
 *   outside that range wraps around the week.
 * @returns Epoch ms of that deadline.
 */
export function getNextShadowReviewDeadlineMs({
  afterMs,
  dueWeekday,
}: {
  afterMs: number;
  dueWeekday: number;
}): number {
  const endOfDueWeekdayAnchorMs =
    END_OF_SUNDAY_AOE_ANCHOR_MS + dueWeekday * ONE_DAY_MS;
  const weeksElapsed =
    Math.floor((afterMs - endOfDueWeekdayAnchorMs) / ONE_WEEK_MS) + 1;
  return endOfDueWeekdayAnchorMs + weeksElapsed * ONE_WEEK_MS;
}

/**
 * When peer-shadow reviews of a section week are due: the first weekly
 * deadline (see {@link getNextShadowReviewDeadlineMs}) strictly after the
 * week's last section ends (see {@link getWeekLastSectionEndMs}). A week whose
 * last section ends exactly at a deadline instant is due at the following one,
 * so the review window is the half-open interval `(lastSectionEnd, due]` and is
 * never empty.
 *
 * @param firstSectionTimestamp - Course anchor ISO string (week 1's first section).
 * @param sectionTimeDelta - Hours from the first to the last section of a week
 *   (the last section's time index).
 * @param sectionWeek - 1-based section week.
 * @param dueWeekday - The weekday whose end is the deadline, numbered as
 *   `Date.prototype.getDay` does (course `shadowReviewDueWeekday`).
 * @returns Epoch ms of the review-due instant.
 * @throws If `firstSectionTimestamp` is unparseable (from `computeSectionInstant`).
 */
export function getShadowReviewDueMs({
  firstSectionTimestamp,
  sectionTimeDelta,
  sectionWeek,
  dueWeekday,
  clock,
}: {
  firstSectionTimestamp: string;
  sectionTimeDelta: number;
  sectionWeek: number;
  dueWeekday: number;
  clock?: SectionClock;
}): number {
  return getNextShadowReviewDeadlineMs({
    afterMs: getWeekLastSectionEndMs({
      firstSectionTimestamp,
      sectionTimeDelta,
      sectionWeek,
      clock,
    }),
    dueWeekday,
  });
}

/**
 * The first end of an Anywhere-on-Earth day at or after `atMs`. An `atMs`
 * that is exactly a day end returns itself, so the result lies in the
 * half-open interval `[atMs, atMs + 1 day)`.
 *
 * @param atMs - The instant (epoch ms).
 * @returns Epoch ms of that day end.
 */
function getEndOfAoeDayAtOrAfterMs(atMs: number): number {
  const daysElapsed = Math.ceil(
    (atMs - END_OF_SUNDAY_AOE_ANCHOR_MS) / ONE_DAY_MS,
  );
  return END_OF_SUNDAY_AOE_ANCHOR_MS + daysElapsed * ONE_DAY_MS;
}

/**
 * When one section's peer-shadow review is due, for a course that guarantees
 * reviewers a minimum time after the section they review. Without that
 * setting it is the week's deadline unchanged. With it, it is the later of the
 * week's deadline and the first end of an Anywhere-on-Earth day at least
 * `minDaysAfterSection` days after the section ends: the review window is the
 * half-open interval `(sectionEnd, due]` with `due ≥ sectionEnd + minDays`.
 *
 * @param weeklyDueMs - The week's deadline (see {@link getShadowReviewDueMs}).
 * @param sectionEndMs - Epoch ms the reviewed section's scheduled slot ends.
 *   Null or omitted (an untimed section): the week's deadline.
 * @param minDaysAfterSection - The course's minimum review time, in days.
 *   Omitted, or not a positive finite number: the week's deadline.
 * @returns Epoch ms of the review-due instant.
 * @throws Never.
 */
export function getShadowReviewDueMsForSection({
  weeklyDueMs,
  sectionEndMs,
  minDaysAfterSection,
}: {
  weeklyDueMs: number;
  sectionEndMs?: number | null;
  minDaysAfterSection?: number;
}): number {
  if (
    minDaysAfterSection === undefined ||
    !Number.isFinite(minDaysAfterSection) ||
    minDaysAfterSection <= 0 ||
    sectionEndMs === undefined ||
    sectionEndMs === null
  ) {
    return weeklyDueMs;
  }
  return Math.max(
    weeklyDueMs,
    getEndOfAoeDayAtOrAfterMs(sectionEndMs + minDaysAfterSection * ONE_DAY_MS),
  );
}

/**
 * Computes the current section week (1-based) given timing fields
 * and an observation time.
 *
 * Formula (matches the webapp):
 * ```
 * currentSectionWeek = max(1, ceil((now - lastSectionTimestamp) / 1 week) + 1)
 * ```
 *
 * Equivalently, week `w` is the half-open interval
 * `(lastSection(w - 2), lastSection(w - 1)]`, where `lastSection(k)` starts
 * 0-based week `k`'s last section: the week rolls over just after the
 * previous week's last section starts. When the clock follows the course
 * zone those boundaries are the zone-aware instants, so they move with the
 * meetings across a DST change.
 *
 * @param firstSectionTimestamp - Course anchor ISO string.
 * @param sectionTimeDelta - Hours from the first to the last section of a
 *   week.
 * @param nowMs - The observation time.
 * @param clock - The course's {@link SectionClock}. Omitted: fixed-instant
 *   recurrence.
 * @return The 1-based section week, unclamped, or `undefined` when the
 *   anchor is missing or unparseable.
 * @throws Never.
 */
export function getCurrentSectionWeek({
  firstSectionTimestamp,
  sectionTimeDelta,
  nowMs,
  clock = {},
}: {
  firstSectionTimestamp: string;
  sectionTimeDelta: number;
  nowMs: number;
  clock?: SectionClock;
}): number | undefined {
  const lastSection = calcLastSectionTimestamp({
    firstSectionTimestamp,
    sectionTimeDelta,
  });
  if (!lastSection) return undefined;

  // subtract last section date from current date and take the delta (round to the nearest week)
  // we add 1 because the first week will be below the lastSectionDate
  const elapsedMs = nowMs - lastSection.getTime();
  const fixedWeek = Math.ceil(elapsedMs / ONE_WEEK_MS) + ONE_INDEX_OFFSET;
  if (!isFollowingCourseTimezone(clock)) {
    return fixedWeek;
  }

  // Zone-aware boundaries sit within one offset change of the fixed ones,
  // so these loops step at most once.
  const lastSectionMs = (weekIndex: number) =>
    calcLastSectionTimestamp({
      firstSectionTimestamp,
      sectionTimeDelta,
      weekIndex,
      clock,
    })?.getTime() ?? Number.NaN;
  let week = fixedWeek;
  while (nowMs > lastSectionMs(week - 1)) {
    week += 1;
  }
  while (nowMs <= lastSectionMs(week - 2)) {
    week -= 1;
  }
  return week;
}

/**
 * Computes the most recently completed section week for a course.
 *
 * This is `getCurrentSectionWeek() - 1`, clamped to `[1, nWeeks]`.
 * Returns `undefined` if no week has been completed yet or the course
 * is past its final week.
 */
export function getCompletedSectionWeek({
  firstSectionTimestamp,
  sectionTimeDelta,
  nWeeks,
  nowMs,
  clock,
}: {
  firstSectionTimestamp: string;
  sectionTimeDelta: number;
  nWeeks: number;
  nowMs: number;
  clock?: SectionClock;
}): number | undefined {
  const currentWeek = getCurrentSectionWeek({
    firstSectionTimestamp,
    sectionTimeDelta,
    nowMs,
    clock,
  });
  if (currentWeek === undefined) return undefined;

  const completedWeek = currentWeek - 1;
  if (completedWeek < 1 || completedWeek > nWeeks) return undefined;

  return completedWeek;
}
